import { DropZone } from "@/components/DropZone";
import { Text } from "@/components/Typography/Text";
import { useTranslation } from "@/lib/i18n";
import { memo, useCallback, useMemo } from "react";
import type { FieldRenderInputProps } from "../../../types";
import { BaseField, type BaseFieldProps } from "../BaseField";
import { ReadOnlyFileList } from "./ReadOnlyFileList";
import {
  useFileDropzone,
  useFileOperations,
  useFileState,
  useFileValidation,
} from "./hooks";
import type { FileInfo } from "./types";
import {
  convertFileValueToFileInfo,
  convertToDropZoneItems,
  extractFileConstraints,
} from "./utils";

export const FileField = memo(
  ({
    fieldId,
    field,
    disabled,
    readOnly,
    layout,
    className,
    "data-testid": testId,
  }: BaseFieldProps) => {
    const { t } = useTranslation("translation", {
      keyPrefix: "features.formFactory.fileField",
    });

    const constraints = useMemo(
      () => extractFileConstraints(field.file),
      [field.file],
    );

    const helpText = useMemo(() => {
      const parts: string[] = [];

      if (constraints.multiple && constraints.maxFiles > 1) {
        parts.push(t("maxFiles", { count: constraints.maxFiles }));
      }

      if (constraints.maxSize > 0) {
        const sizeMB = Math.round(constraints.maxSize / (1024 * 1024));
        parts.push(t("maxFileSize", { size: sizeMB }));
      }

      if (constraints.acceptedMimeTypes.length > 0) {
        const formats = constraints.acceptedMimeTypes
          .map((mime) => mime.split("/")[1]?.toUpperCase())
          .filter((format): format is string => Boolean(format));
        if (formats.length > 0) {
          parts.push(t("supportedFormats", { formats: formats.join(", ") }));
        }
      } else if (constraints.acceptedExtensions.length > 0) {
        parts.push(
          t("supportedFormats", {
            formats: constraints.acceptedExtensions.join(", "),
          }),
        );
      }

      const baseHelpText = field.description || "";
      const constraintText = parts.length > 0 ? ` (${parts.join(", ")})` : "";
      return baseHelpText + constraintText;
    }, [constraints, field.description, t]);

    const {
      fileInfos,
      setFileInfos,
      isInitialized,
      setIsInitialized,
      uploadError,
      setUploadError,
      onChangeRef,
    } = useFileState();

    const validateFiles = useFileValidation(field);

    const notifyFormChange = useCallback(
      (files: readonly FileInfo[]): void => {
        const onChange = onChangeRef.current;
        if (!onChange) return;

        const fileInfos = files.map((info) => {
          if (info.type === "local") {
            return info.file;
          } else {
            return info.fileInfo;
          }
        });

        onChange(fileInfos);
      },
      [onChangeRef],
    );

    const { removeFile } = useFileOperations(setFileInfos, notifyFormChange);

    const { getRootProps, getInputProps, isDragActive } = useFileDropzone(
      constraints,
      Boolean(disabled),
      Boolean(readOnly),
      validateFiles,
      setUploadError,
      setFileInfos,
      notifyFormChange,
    );

    const renderInput = useCallback(
      (inputProps: FieldRenderInputProps) => {
        onChangeRef.current = inputProps.onChange;

        if (!isInitialized && !inputProps.readOnly) {
          if (inputProps.value) {
            const fileInfos = convertFileValueToFileInfo(inputProps.value);
            setFileInfos(fileInfos);
          } else {
            setFileInfos([]);
          }
          setIsInitialized(true);
        }

        if (inputProps.readOnly) {
          // 読み取り専用から再度編集になった時に、現在登録されているFile情報からfileInfosを再生成させるためにここでIsInitializedをfalseにしている
          // この中でfileInfosを再生成してしまうと、useCallbackがfileInfosの変更を検知して無限ループとなるのでIsInitializedを設定して編集状態となった際の初期化処理で実行させている
          setIsInitialized(false);
          const fileInfos = convertFileValueToFileInfo(inputProps.value);
          if (fileInfos.length === 0) {
            return (
              <Text variant="pSmall" className="text-neutral-muted-on-fill">
                {t("noFilesSelected")}
              </Text>
            );
          }
          return <ReadOnlyFileList fileInfos={fileInfos} />;
        }

        // 編集モードではDropZoneを使用
        return (
          <DropZone
            variant="file"
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragActive={isDragActive}
            acceptedFiles={[]}
            onResetButtonClick={() => {}}
            uploadCompleted={true}
            uploadError={uploadError}
            fileItems={convertToDropZoneItems(fileInfos)}
            onRemoveFile={removeFile}
            disabled={disabled}
            helpText={helpText}
          />
        );
      },
      [
        fileInfos,
        getRootProps,
        getInputProps,
        setFileInfos,
        setIsInitialized,
        isDragActive,
        disabled,
        removeFile,
        isInitialized,
        uploadError,
        helpText,
        onChangeRef,
        t,
      ],
    );

    const enhancedField = useMemo(
      () => ({
        ...field,
        description: helpText,
      }),
      [field, helpText],
    );

    return (
      <BaseField
        fieldId={fieldId}
        field={enhancedField}
        disabled={disabled}
        readOnly={readOnly}
        layout={layout}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

FileField.displayName = "FileField";
